import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "reactstrap";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import DetalleContenedor from "./DetalleContenedor";
import ModalAplicar from "./ModalAplicar";
import ModalGenerar from "./ModalGenerar";
import TabContenedores from "./TabContenedores";
import TabMovimientos from "./TabMovimientos";
import TabReportes from "./TabReportes";
import { auth } from "./finanzasUtils";

const URL_CONCEPTOS = process.env.REACT_APP_URL_CONCEPTOS_GASTOS;
const URL_BROKERS = process.env.REACT_APP_URL_BROKERS;
const URL_TC = process.env.REACT_APP_URL_TIPOS_CAMBIO;

const TABS = [
  { id: "contenedores", label: "Contenedores", icon: "fas fa-ship" },
  { id: "movimientos", label: "Movimientos", icon: "fas fa-exchange-alt" },
  { id: "reportes", label: "Reportes y gráficas", icon: "fas fa-chart-bar" },
  { id: "consultas", label: "Estados de cuenta", icon: "fas fa-file-invoice-dollar" },
];

// Módulo único de cargos, abonos, gastos y pagos
export default function Finanzas() {
  const { user } = useContext(AuthContext);
  const [t] = useTranslation("global");
  const { search } = useLocation();
  const [tab, setTab] = useState(new URLSearchParams(search).get("tab") || "contenedores");
  const [refresco, setRefresco] = useState(0);
  const [accion, setAccionState] = useState(null); // { modo, contenedor }
  const [detalle, setDetalle] = useState(null);
  const [conceptos, setConceptos] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [tcDia, setTcDia] = useState("");

  const esAdmin = user.tipo === "Admin";
  const puede = !!user.menuAdmin;

  useEffect(() => {
    if (!puede) return;
    axios.get(URL_CONCEPTOS, auth()).then(({ data }) => setConceptos(data)).catch(() => setConceptos([]));
    axios.get(URL_BROKERS, auth()).then(({ data }) => setBrokers(data)).catch(() => setBrokers([]));
    axios.get(`${URL_TC}Vigente`, auth()).then(({ data }) => setTcDia((data && data.tipoCambio) || "")).catch(() => setTcDia(""));
  }, [puede]);

  if (!puede) return <Header />;

  const accionar = (modo, contenedor) => setAccionState({ modo, contenedor });
  const recarga = () => setRefresco((r) => r + 1);

  const CONSULTAS = [
    {
      titulo: "Clientes",
      items: [
        { label: "Estado de cuenta", desc: "Notas, cargos y abonos por cliente", href: "/EstadoCuenta", icon: "fas fa-file-invoice-dollar", ok: user.estadoCuentaClientes },
        { label: "Estado de cuenta Liberación y Flete", desc: "Cada tipo con su saldo", href: "/EstadoCuentaCargos", icon: "fas fa-layer-group", ok: user.menuAdmin },
        { label: "Estado de cuenta ML", desc: "Estado de cuenta ML", href: "/EstadoCuentaML", icon: "fas fa-file-alt", ok: user.estadoCuentaML },
        { label: "Abonos y recibos", desc: "Editar abonos, recibos y comprobantes", href: "/ListadoAbonos", icon: "fas fa-hand-holding-usd", ok: user.menuAbonos },
        { label: "Abono a varios contenedores", desc: "Un pago repartido entre las notas del cliente", href: "/AbonosCreate", icon: "fas fa-coins", ok: user.abonosCreate },
      ],
    },
    {
      titulo: "Brokers",
      items: [
        { label: "Estado de cuenta", desc: "Saldo por broker", href: "/EstadoCuentaCXP", icon: "fas fa-file-invoice-dollar", ok: user.estadoCuentaBrokers && esAdmin },
        { label: "Gastos y autorización", desc: "Listado de gastos por autorizar", href: "/Gastos", icon: "fas fa-receipt", ok: user.menu_gastos },
        { label: "Pagos", desc: "Listado de pagos a brokers", href: "/ListadoAbonosGastos", icon: "fas fa-money-check-alt", ok: user.menuPagos && esAdmin },
        { label: "Pago a varios contenedores", desc: "Un pago repartido entre los gastos del broker", href: "/AbonosGastosCreate", icon: "fas fa-coins", ok: user.pagosCreate && esAdmin },
        { label: "Conceptos", desc: "Catálogo de conceptos", href: "/ConceptosGastos", icon: "fas fa-list-ul", ok: user.menu_gastos && esAdmin },
      ],
    },
  ];

  return (
    <>
      <Header />
      <div className="card container col-sm-11 rc-page">
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <span className="page-title-icon"><i className="fas fa-coins" /></span>
              {t("Finanzas")}
            </h3>
            <p className="page-subtitle">Cargos y abonos del cliente, gastos y pagos a brokers, por contenedor</p>
          </div>
        </div>

        <div className="fz-tabs" role="tablist">
          {TABS.map((x) => (
            <button key={x.id} type="button" role="tab" aria-selected={tab === x.id} className={tab === x.id ? "on" : ""} onClick={() => setTab(x.id)}>
              <i className={x.icon} /> {x.label}
            </button>
          ))}
        </div>

        {tab === "contenedores" ? <TabContenedores user={user} accion={accionar} verDetalle={setDetalle} refresco={refresco} /> : null}
        {tab === "movimientos" ? <TabMovimientos user={user} refresco={refresco} onCambio={recarga} /> : null}
        {tab === "reportes" ? <TabReportes refresco={refresco} /> : null}
        {tab === "consultas" ? (
          <div className="fz-consultas">
            {CONSULTAS.map((g) => (
              <div key={g.titulo}>
                <h5 className="fz-titulo">{g.titulo}</h5>
                {g.items.filter((i) => i.ok).map((i) => (
                  <Button key={i.href} tag="a" href={i.href} color="link" className="fz-link">
                    <i className={i.icon} />
                    <span><strong>{i.label}</strong><small>{i.desc}</small></span>
                  </Button>
                ))}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <DetalleContenedor contenedor={detalle} cerrar={() => setDetalle(null)} user={user} accion={accionar} refresco={refresco} onCambio={recarga} />

      <ModalGenerar
        abierto={accion && (accion.modo === "cargos" || accion.modo === "gastos") ? accion : null}
        cerrar={() => setAccionState(null)}
        user={user}
        conceptos={conceptos}
        brokers={brokers}
        tcDia={tcDia}
        onSaved={recarga}
      />
      <ModalAplicar
        abierto={accion && (accion.modo === "abonos" || accion.modo === "pagos") ? accion : null}
        cerrar={() => setAccionState(null)}
        onSaved={recarga}
      />
    </>
  );
}
